import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { useSnackbar } from 'notistack';
import { observer } from 'mobx-react';
import {
  Box,
  Paper,
  Button,
  FormControl,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid2';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { routes } from 'config/index';
import CampaignsService from 'services/campaignsService';
import domainData from 'store/domainStore';
import Modal from 'components/common/customModal';
import { getMessage } from 'helpers/helper';

import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import ButtonsGroup from 'components/common/customBtnGroup';
import WineLoversFilters from './components/wineLovers';
import CampaignMessage from './components/CampaignMessage';
import M from 'messages';
import classes from './styles';

const CreateCampaignWrapper = observer(({ isEdit, isCreate }) => {
  const isUserSuperAdmin = domainData.userIsSuperAdmin();
  const currentSPId = domainData.getCurrentSP();
  return (
    <CreateCampaign isEdit={isEdit} isCreate={isCreate} currentSPId={currentSPId} isUserSuperAdmin={isUserSuperAdmin} />
  );
});

const CreateCampaign = observer(({ isEdit = false, isCreate = false, isUserSuperAdmin, _currentSPId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const navigate = useNavigate();
  const pageContentRef = useRef(null);
  const editorWrapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Details, 1: Message Builder
  const [data, setData] = useState({
    name: '',
    emailSubject: '',
    type: 'Event',
    messageHtml: '',
    messageMjml: '',
    serviceProviderId: '',
    business: [],
    country: [],
    state: [],
    regDate: {
      from: '',
      to: '',
    },
    role: [],
    partnerAudience: ['followers'],
  });
  const [createAbility, setCreateAbility] = useState(false);
  const [original, setOriginal] = useState(null);
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [editorHeight, setEditorHeight] = useState(0);

  const [accessLoaded, setAccessLoaded] = useState(false);
  const [actingSpId, setActingSpId] = useState('');
  const [spList, setSpList] = useState([]);
  const [countToSendCampaign, setCountToSendCampaign] = useState({});

  const getRecipientsCount = async () => {
    try {
      const payload = {
        type: data.type,
        business: data.business,
        country: data.country,
        state: data.state,
        regDate: data.regDate,
        role: data.role,
        partnerAudience: data.partnerAudience,
      };
      if (isUserSuperAdmin) {
        payload.serviceProviderId = actingSpId;
      }
      const res = await CampaignsService.recipientsCount(payload);
      setRecipientsCount(res.data?.total || 0);
    } catch (err) {
      setRecipientsCount(0);
    }
  };
  // Load initial filter options
  useEffect(() => {
    if (accessLoaded && (isUserSuperAdmin || createAbility)) {
      getRecipientsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.type,
    data.business,
    data.country,
    data.state,
    data.regDate,
    data.role,
    data.partnerAudience,
    isUserSuperAdmin,
    createAbility,
    accessLoaded,
  ]);

  const canSave = useMemo(() => {
    const baseReady =
      !!data.name && !!data.type && !!data.messageMjml && !!data.emailSubject && data.partnerAudience.length;
    if (isUserSuperAdmin) {
      return baseReady && !!actingSpId;
    }
    return baseReady;
  }, [data, isUserSuperAdmin, actingSpId]);

  const canSend = useMemo(() => {
    const baseReady = !!data.name && !!data.type && !!data.messageMjml && !!data.emailSubject && !!recipientsCount;
    if (isUserSuperAdmin) return baseReady && !!actingSpId;
    return baseReady;
  }, [data, recipientsCount, isUserSuperAdmin, actingSpId]);

  useEffect(() => {
    const init = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const res = await CampaignsService.getCampaign(id);
          setData({
            name: res.data.name || '',
            emailSubject: res.data.emailSubject || '',
            type: res.data.type || 'Deal',
            role: res.data.audienceDefinition?.role || [],
            state: res.data.audienceDefinition?.state || [],
            country: res.data.audienceDefinition?.country || [],
            regDate: res.data.audienceDefinition?.regDate || {},
            business: res.data.audienceDefinition?.business || [],
            partnerAudience: res.data.audienceDefinition?.partnerAudience || ['followers'],
            messageHtml: res.data.messageHtml || '',
            messageMjml: res.data.messageMjml || '',
            serviceProviderId: res.data.serviceProviderId || '',
          });
          setActingSpId(res.data.serviceProviderId || '');
          setSpList([res.data.serviceProvider]);
          /*const audienceDef = res.data.audienceDefinition || {};
          const wlRoot = audienceDef.wineLoversFilters || (audienceDef.sa && audienceDef.sa.wineLoversFilters) || {};
          if (isUserSuperAdmin) {
            const chosenSp =
              res.data.serviceProviderId ||
              (wlRoot && Array.isArray(wlRoot.partnerIds) && wlRoot.partnerIds[0]) ||
              domainData.getCurrentSP();
            if (chosenSp) setActingSpId(String(chosenSp));
          }*/
          setOriginal(res.data);
        } catch (err) {
          setLoading(false);
        }
      }
      if (!isEdit) {
        setLoading(false);
        if (isUserSuperAdmin) {
          const list = await domainData.getSPListNames();
          setSpList(list || []);
          setActingSpId(String(list[0].id));
        }
      }
    };
    init();
  }, [id, isEdit, isUserSuperAdmin]);

  // Load initial filter options
  useEffect(() => {
    const loadCampaignAccess = async () => {
      setLoading(true);
      try {
        const {
          data: { createAbility, countToSendCampaign, campaignMonthlyCount, sentCampaignsCount, planException },
        } = await CampaignsService.getCreateAbility({ serviceProviderId: isUserSuperAdmin ? actingSpId : null });
        setCreateAbility(createAbility);
        setCountToSendCampaign({
          countToSendCampaign,
          campaignMonthlyCount,
          sentCampaignsCount,
          planException,
        });
        setAccessLoaded(true);
      } catch (_e) {
        enqueueSnackbar(getMessage(_e?.response?.data, 'error'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    if (isUserSuperAdmin && !actingSpId) {
      return;
    }
    loadCampaignAccess();
  }, [actingSpId, isUserSuperAdmin, enqueueSnackbar]);

  // For non-SA, ensure current partner is preselected and enforced in WL filters
  /*useEffect(() => {
    if (!isUserSuperAdmin && currentSPId) {
      setWlFilters(prev => ({
        ...prev,
        partnerIds: [currentSPId],
        partnerAudience: prev.partnerAudience && prev.partnerAudience.length ? prev.partnerAudience : ['followers'],
      }));
    }
  }, [isUserSuperAdmin, currentSPId]);*/

  // For SA, enforce wlFilters.partnerIds from actingSpId and persist to domain store
  /* useEffect(() => {
    if (isUserSuperAdmin) {
      if (actingSpId) {
        setWlFilters(prev => ({
          ...prev,
          partnerIds: [Number(actingSpId)],
          partnerAudience: prev.partnerAudience && prev.partnerAudience.length ? prev.partnerAudience : ['followers'],
        }));
        domainData.setCurrentSP(String(actingSpId));
      }
    }
  }, [isUserSuperAdmin, actingSpId]);*/

  const isSent = isEdit && original?.status === 'Sent';

  // Compute available height for editor tab
  useEffect(() => {
    setEditorHeight(window.innerHeight - 252);
  }, []);

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const saveData = async () => {
    if (!canSave) return;
    try {
      setSubmitLoading(true);
      const audienceDefinition = {
        role: data.role,
        state: data.state,
        country: data.country,
        regDate: data.regDate,
        business: data.business,
        partnerAudience: data.partnerAudience,
      };

      const payload = {
        name: data.name,
        emailSubject: data.emailSubject,
        type: data.type,
        audienceDefinition,
        messageMjml: data.messageMjml,
        serviceProviderId: isUserSuperAdmin ? actingSpId : null,
      };
      let campaignId = null;
      if (isEdit) {
        const res = await CampaignsService.update(id, payload);
        campaignId = res.data.id;
      } else {
        const res = await CampaignsService.create(payload);
        campaignId = res.data.id;
      }
      return { success: true, id: campaignId };
    } catch (err) {
      setSubmitLoading(false);
      return { success: false, err };
    }
  };
  const onSubmit = async e => {
    e.preventDefault();
    const { success, err } = await saveData();
    if (!success) {
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      return;
    }
    enqueueSnackbar(M.get('actionMsg.success.operationSucceeded'), { variant: 'success' });
    navigate(routes.campaigns.path);
  };

  const handleDiscard = () => navigate(routes.campaigns.path);

  const handleOpenSendConfirm = () => setSendConfirmOpen(true);
  const handleCloseSendConfirm = () => setSendConfirmOpen(false);

  const handleSaveAndSend = async () => {
    if (!canSave) return;
    try {
      setSendLoading(true);
      const { success, err, id: campaignId } = await saveData();
      if (!success) {
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
        return;
      }
      // Send campaign
      await CampaignsService.sendCampaign(campaignId);
      enqueueSnackbar(M.get('actionMsg.success.operationSucceeded'), { variant: 'success' });
      navigate(routes.campaigns.path);
    } catch (err) {
      enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
    } finally {
      setSendLoading(false);
      setSendConfirmOpen(false);
      setSubmitLoading(false);
    }
  };

  const saveAndSendButtonDisabled = disableSave => (
    <Button
      variant="contained"
      color="primary"
      onClick={handleOpenSendConfirm}
      disabled={!canSave || !canSend || sendLoading || disableSave}
      sx={classes.footerButton}>
      {sendLoading ? <CircularProgress size={16} /> : M.get('campaigns.view.footer.saveAndSend')}
    </Button>
  );

  const saveDraftButton = (disableSave = false) => (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      onClick={onSubmit}
      sx={classes.footerButton}
      disabled={!canSave || submitLoading || disableSave}>
      {submitLoading ? <CircularProgress size={16} /> : M.get('campaigns.view.footer.save')}
    </Button>
  );

  const configOptions = {
    typeGroup: {
      label: 'pages.type',
      name: 'type',
      fields: [
        { value: 'Event', label: 'pages.typeEvent' },
        { value: 'Deal', label: 'pages.typeDeal' },
        { value: 'News', label: 'pages.typeNews' },
      ],
    },
  };

  if (loading) return <Loading />;

  return (
    <Box sx={classes.page}>
      <PageHeader
        generalPageTitle={`${M.get('pages.campaigns')} | ${isCreate ? M.get('actions.create') : original?.name || ''}`}
        onlyGeneralPageTitle
      />
      <Box sx={{ ...classes.pageContent, overflowY: activeTab === 1 ? 'hidden' : 'auto' }} ref={pageContentRef}>
        <form onSubmit={onSubmit} noValidate>
          <Box component={Paper} sx={classes.boxContainer} pb={2} px={3} pt={1}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile>
                <Tab label={M.get('campaigns.view.campaigns')} />
                <Tab label={M.get('campaigns.view.campaignMessageBuilder.label')} />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <>
                <Grid container spacing={3} mb={3} mt={1}>
                  {isUserSuperAdmin && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Autocomplete
                            options={spList || []}
                            getOptionLabel={opt => opt?.name || ''}
                            value={
                              (spList || []).find(
                                opt =>
                                  String(opt.id || opt.value) === String(isEdit ? data.serviceProviderId : actingSpId),
                              ) || null
                            }
                            onChange={(_, v) => setActingSpId(v ? String(v.id || v.value) : '')}
                            disableClearable
                            disabled={isEdit}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label={M.get('wineLists.serviceProvider')}
                                placeholder={M.get('wineLists.serviceProvider')}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }} alignContent="center">
                        {countToSendCampaign.planException ? (
                          <Typography sx={classes.campaignQuotaText}>
                            <b>Campaign Quota: </b> Unlimited usage.
                          </Typography>
                        ) : (
                          <Typography sx={classes.campaignQuotaText}>
                            <b>Campaign Quota: </b> {countToSendCampaign.sentCampaignsCount}/
                            {countToSendCampaign.campaignMonthlyCount} used this month.
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Divider />
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label={M.get('campaigns.view.campaignName.label')}
                        value={data.name}
                        onChange={e => handleChange('name', e.target.value)}
                        required
                        disabled={isSent}
                        slotProps={{
                          // Props for the React Input component (OutlinedInput/FilledInput/Input)
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip placement="bottom" title={M.get('campaigns.view.campaignName.tooltip')}>
                                  <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                          htmlInput: {
                            maxLength: 255,
                          },
                        }}
                        helperText={
                          <Box component="span" sx={classes.helperDiv}>
                            <Box component="span">{data.name?.length || 0}/255</Box>
                          </Box>
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label={M.get('campaigns.view.emailSubject.label')}
                        value={data.emailSubject}
                        onChange={e => handleChange('emailSubject', e.target.value)}
                        disabled={isSent}
                        required
                        slotProps={{
                          // Props for the React Input component (OutlinedInput/FilledInput/Input)
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip placement="bottom" title={M.get('campaigns.view.emailSubject.tooltip')}>
                                  <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                          htmlInput: {
                            maxLength: 255,
                          },
                        }}
                        helperText={
                          <Box component="span" sx={classes.helperDiv}>
                            <Box component="span">{data.emailSubject?.length || 0}/255</Box>
                          </Box>
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {/* use ButtonsGroup here */}
                    <Box display="flex" alignItems="center">
                      <Typography sx={classes.fieldHeaderText} component="h1">
                        {M.get('campaigns.view.campaignType.label')}
                      </Typography>
                      {
                        <Tooltip
                          title={
                            <Typography
                              sx={{
                                fontSize: '11px',
                                lineHeight: 'normal',
                                color: 'inherit',
                              }}>
                              {M.get('campaigns.view.campaignType.tooltip')}
                            </Typography>
                          }
                          placement="top">
                          <HelpOutlineIcon sx={classes.helpIcon} />
                        </Tooltip>
                      }
                    </Box>
                    <Box>
                      <ButtonsGroup
                        groupObject={configOptions.typeGroup}
                        handleChange={handleChange}
                        value={data.type}
                        disabled={isSent}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    {(isUserSuperAdmin || createAbility) && (
                      <>
                        <Box display="flex" alignItems="center">
                          <Typography sx={classes.fieldHeaderText} component="h1">
                            {M.get('campaigns.view.targetWineLoverAudience.label')}
                          </Typography>
                          {
                            <Tooltip
                              title={
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    lineHeight: 'normal',
                                    color: 'inherit',
                                  }}>
                                  {M.get('campaigns.view.targetWineLoverAudience.tooltip')}
                                </Typography>
                              }
                              placement="top">
                              <HelpOutlineIcon sx={classes.helpIcon} />
                            </Tooltip>
                          }
                        </Box>
                        <Box display="flex" alignItems="center" mt={1} mb={2}>
                          <Typography>{M.get('campaigns.view.campaignReach.label')}</Typography>
                          {
                            <Tooltip
                              title={
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    lineHeight: 'normal',
                                    color: 'inherit',
                                  }}>
                                  {M.get('campaigns.view.campaignReach.tooltip')}
                                </Typography>
                              }
                              placement="top">
                              <HelpOutlineIcon sx={classes.helpIcon} />
                            </Tooltip>
                          }
                          <Typography>: {recipientsCount}</Typography>
                        </Box>
                      </>
                    )}
                    <Box>
                      <WineLoversFilters onChange={isSent ? () => {} : handleChange} data={data} disabled={isSent} />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                </Grid>
              </>
            )}

            {activeTab === 1 && (
              <Grid container spacing={3} mb={3} mt={1}>
                <Grid size={{ xs: 12 }}>
                  <Box ref={editorWrapRef}>
                    {isSent ? (
                      <Box
                        sx={{
                          border: '1px solid #eee',
                          borderRadius: 1,
                          p: 2,
                          background: '#fff',
                          height: editorHeight,
                          overflow: 'auto',
                        }}>
                        <div dangerouslySetInnerHTML={{ __html: original?.messageHtml || data.messageHtml || '' }} />
                      </Box>
                    ) : (
                      <CampaignMessage
                        campaignId={id}
                        valueMjml={data.messageMjml}
                        onChangeMjml={mjml => setData(prev => ({ ...prev, messageMjml: mjml }))}
                        onRendered={html => setData(prev => ({ ...prev, messageHtml: html }))}
                        heightPx={editorHeight}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </form>
      </Box>
      <Box sx={classes.pageFooter}>
        <Box>
          <Grid container sx={classes.footerPart}>
            <Button variant="text" color="primary" onClick={handleDiscard} sx={classes.footerButton}>
              {M.get('campaigns.view.footer.discard')}
            </Button>
            {!isSent && (
              <>
                {createAbility || countToSendCampaign.campaignMonthlyCount ? (
                  saveDraftButton()
                ) : (
                  <Tooltip title={M.get('campaigns.createLimit')} placement="top">
                    <span>{saveDraftButton(true)}</span>
                  </Tooltip>
                )}
                {isUserSuperAdmin || createAbility ? (
                  saveAndSendButtonDisabled()
                ) : (
                  <Tooltip title={M.get('campaigns.createLimit')} placement="top">
                    <span>{saveAndSendButtonDisabled(true)}</span>
                  </Tooltip>
                )}
              </>
            )}
          </Grid>
        </Box>
      </Box>
      <Modal
        open={sendConfirmOpen}
        handleClose={handleCloseSendConfirm}
        handleSubmit={handleSaveAndSend}
        title={M.get('campaigns.sendConfirmationTitle')}
        rightBtnText={M.get('campaigns.view.footer.sendCampaign')}
        submitLoading={sendLoading}
        disableSave={sendLoading}>
        <Typography>{M.get('campaigns.sendConfirmationText')}</Typography>
      </Modal>
    </Box>
  );
});

CreateCampaign.propTypes = {
  isEdit: PropTypes.bool,
  isCreate: PropTypes.bool,
  isUserSuperAdmin: PropTypes.bool,
  currentSPId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CreateCampaignWrapper;
